import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import {
  CancelActionValue,
  DeclineActionValue,
  SendOfferActionValue,
} from '#/shared/api/generated/shipment/models'
import type {
  AcceptAction,
  TransitionCarrierRequestBody,
} from '#/shared/api/generated/shipment/models'
import { useTransitionCarrierRequest } from '#/shared/api/generated/shipment/theCargoShipment'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { toast } from '#/shared/ui/sonner'

import type {
  CarrierRequestPending,
  CarrierRequestPendingAction,
} from './types'

interface UseTransitionRequestOptions {
  shipmentId?: string
  onSuccess?: () => void
}

function derivePendingAction(
  data: TransitionCarrierRequestBody,
): CarrierRequestPendingAction {
  if (data.action === 'decline') return 'decline'
  if (data.action === 'cancel') return 'cancel'
  if (data.action === 'send_offer') return 'sendOffer'
  return data.counter_offer_price != null ? 'counter' : 'accept'
}

export function useTransitionRequest({
  shipmentId,
  onSuccess,
}: UseTransitionRequestOptions = {}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const mutation = useTransitionCarrierRequest()

  /**
   * `keepOpen` marks the transitions that only rework the row in place (a
   * decline still leaves the request listed, ready for a fresh offer) — those
   * must not fire `onSuccess`, which closes the dialog.
   */
  const run = async (
    requestId: string,
    data: TransitionCarrierRequestBody,
    successKey: string,
    errorKey: string,
    { keepOpen = false }: { keepOpen?: boolean } = {},
  ) => {
    try {
      await mutation.mutateAsync({ requestId, data })
      queryClient.invalidateQueries({ queryKey: ['/api/v1/carrier-requests'] })
      if (shipmentId) {
        queryClient.invalidateQueries({
          queryKey: [`/api/v1/shipments/${shipmentId}`],
        })
      }
      toast.success(t(successKey))
      if (!keepOpen) onSuccess?.()
    } catch (error) {
      toast.error(getApiErrorDetail(error) ?? t(errorKey))
    }
  }

  const pending: CarrierRequestPending | null = mutation.isPending
    ? {
        requestId: mutation.variables.requestId,
        action: derivePendingAction(mutation.variables.data),
      }
    : null

  return {
    accept: (requestId: string) =>
      run(
        requestId,
        { action: 'accept' } satisfies AcceptAction,
        'carrierRequests.acceptSuccess',
        'carrierRequests.acceptError',
      ),
    counterOffer: (requestId: string, amount: number) =>
      run(
        requestId,
        {
          action: 'accept',
          counter_offer_price: amount,
        } satisfies AcceptAction,
        'carrierRequests.counterSuccess',
        'carrierRequests.counterError',
      ),
    decline: (requestId: string) =>
      run(
        requestId,
        DeclineActionValue,
        'carrierRequests.declineSuccess',
        'carrierRequests.declineError',
        { keepOpen: true },
      ),
    cancel: (requestId: string) =>
      run(
        requestId,
        CancelActionValue,
        'carrierRequests.cancelSuccess',
        'carrierRequests.cancelError',
      ),
    sendOffer: (requestId: string) =>
      run(
        requestId,
        SendOfferActionValue,
        'carrierRequests.sendOfferSuccess',
        'carrierRequests.sendOfferError',
      ),
    pending,
  }
}
